import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check, Key } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function TempPasswordModal({ open, onOpenChange, tempPassword, userName, userEmail }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="temp-password-modal">
        <DialogHeader>
          <DialogTitle className="font-bold uppercase text-green-600 flex items-center">
            <Key className="w-5 h-5 mr-2" />
            Temporary Password Generated
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <p className="text-slate-600 text-sm">
            A temporary password has been generated for <strong>{userName || userEmail}</strong>.
          </p>
          
          {/* Password display */}
          <div className="bg-slate-100 border-2 border-slate-200 rounded-lg p-4">
            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Temporary Password</p>
            <div className="flex items-center justify-between">
              <code className="text-2xl font-mono font-bold text-slate-900 tracking-wide">
                {tempPassword}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className={copied ? 'bg-green-50 border-green-300 text-green-600' : ''}
                data-testid="copy-temp-password"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
            <p className="font-semibold text-amber-800 mb-2">Important Instructions:</p>
            <ul className="text-amber-700 space-y-1 list-disc list-inside">
              <li>Share this password with the user via text, email, or phone call</li>
              <li>This password will expire after first use</li>
              <li>The user will be required to create a new password upon login</li>
            </ul>
          </div>

          <Button
            onClick={() => onOpenChange(false)}
            className="w-full bg-slate-900 hover:bg-slate-800"
            data-testid="temp-password-done"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
